import styled, { css } from 'styled-components'
import { media } from '../../../utils/mediaQuery'

// Main container
export const AmenitiesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 20px;

  ${media.tabletDown} {
    padding-bottom: 100px; /* Account for FAB button */
  }
`

export const AmenitiesContentContainer = styled.div`
  margin: 0 ${({ theme }) => theme.spacing.large};

  ${media.tabletDown} {
    margin: 0 ${({ theme }) => theme.spacing.medium};
  }
`

// Header section with statistics and score
export const SectionHeaderScore = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 26px;
  flex-wrap: wrap;
  gap: 12px;

  ${media.tabletDown} {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 16px;
    gap: 10px;
  }
`

export const StatsCards = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;

  ${media.tabletDown} {
    width: 100%;
    justify-content: space-between;
    gap: 8px;
  }
`

export const StatCard = styled.div<{
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'primary'
}>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 20px 26px;
  text-align: center;
  transition: all 0.2s;
  min-width: 112px;
  position: relative;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  /* Disable hover on touch devices */
  @media (hover: none) {
    &:hover {
      border-color: ${({ theme }) => theme.colors.border.light};
      box-shadow: none;
    }
  }

  ${({ variant, theme }) => {
    switch (variant) {
      case 'success':
        return css`
          border-color: ${theme.colors.success};
          &:hover {
            box-shadow: 0 2px 8px ${theme.colors.success}15;
          }
        `
      case 'warning':
        return css`
          border-color: ${theme.colors.warning};
          &:hover {
            box-shadow: 0 2px 8px ${theme.colors.warning}15;
          }
        `
      case 'danger':
        return css`
          border-color: ${theme.colors.danger};
          &:hover {
            box-shadow: 0 2px 8px ${theme.colors.danger}15;
          }
        `
      case 'primary':
        return css`
          border-color: ${theme.colors.primary};
          &:hover {
            box-shadow: 0 2px 8px ${theme.colors.primary}15;
          }
        `
      default:
        return ''
    }
  }}

  ${media.tabletDown} {
    flex: 1;
    min-width: 0;
    padding: 12px 10px;
    border-radius: ${({ theme }) => theme.borderRadius.default};
  }

  ${media.mobileOnly} {
    padding: 12px 8px;
  }
`

export const StatValue = styled.div<{
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'primary'
}>`
  font-size: 26px;
  font-weight: 600;
  color: ${({ theme, variant }) => {
    switch (variant) {
      case 'success':
        return theme.colors.success
      case 'warning':
        return theme.colors.warning
      case 'danger':
        return theme.colors.danger
      case 'primary':
        return theme.colors.primary
      default:
        return theme.colors.text.primary
    }
  }};
  line-height: 1;
  margin-bottom: 3px;

  ${media.tabletDown} {
    font-size: 21px;
    margin-bottom: 2px;
  }

  ${media.mobileOnly} {
    font-size: 18px;
  }
`

export const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 400;

  ${media.tabletDown} {
    font-size: 10px;
  }

  ${media.mobileOnly} {
    font-size: 8px;
  }
`

// Score display component
export const ScoreDisplayComponent = styled.div<{
  variant?: 'success' | 'warning' | 'danger' | 'primary'
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  transition: all 0.2s;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  /* Disable hover on touch devices */
  @media (hover: none) {
    &:hover {
      box-shadow: none;
    }
  }

  ${media.tabletDown} {
    width: 100%;
    justify-content: center;
    padding: 8px 12px;
  }
`

export const ScoreDisplay = styled.div`
  display: flex;
  align-items: baseline;
  gap: 3px;
`

export const ScoreValue = styled.span<{
  variant?: 'success' | 'warning' | 'danger' | 'primary'
}>`
  font-size: 26px;
  font-weight: 600;
  color: ${({ theme, variant }) => {
    switch (variant) {
      case 'success':
        return theme.colors.success
      case 'warning':
        return theme.colors.warning
      case 'danger':
        return theme.colors.danger
      case 'primary':
        return theme.colors.primary
      default:
        return theme.colors.text.primary
    }
  }};

  ${media.tabletDown} {
    font-size: 21px;
  }
`

export const ScoreMax = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  ${media.tabletDown} {
    font-size: 12px;
  }
`

export const ScoreLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;

  ${media.tabletDown} {
    font-size: 10px;
  }
`

// Grid layout for category cards (auto 2-column, collapses to 1 when narrow)
export const AmenitiesCategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 12px;

  ${media.mobileOnly} {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`

// Category card
export const AmenityCategoryCard = styled.div<{ expanded?: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 12px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  /* Disable hover on touch devices */
  @media (hover: none) {
    &:hover {
      border-color: ${({ theme }) => theme.colors.border.light};
      box-shadow: none;
    }
  }

  ${media.tabletDown} {
    padding: 10px;
    border-radius: ${({ theme }) => theme.borderRadius.default};
  }
`

export const AmenityCategoryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
  cursor: pointer;
  /* Touch-friendly tap target */
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;

  ${media.tabletDown} {
    flex-direction: row;
    margin-bottom: 8px;
  }
`

export const AmenityCategoryInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;

  ${media.tabletDown} {
    gap: 10px;
  }
`

export const AmenityCategoryIcon = styled.div<{ color?: string }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  background: ${({ color }) =>
    color ? `${color}1a` : 'rgba(0, 173, 230, 0.1)'};
  color: ${({ color }) => color || '#00ade6'};
  flex-shrink: 0;

  ${media.tabletDown} {
    width: 28px;
    height: 28px;
    font-size: 16px;
    border-radius: ${({ theme }) => theme.borderRadius.default};
  }
`

export const AmenityCategoryDetails = styled.div`
  flex: 1;
  min-width: 0;
`

export const AmenityCategoryTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${media.tabletDown} {
    font-size: 12px;
    letter-spacing: 0.3px;
    margin-bottom: 2px;
  }
`

export const AmenitySubcategories = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  ${media.tabletDown} {
    gap: 5px 8px;
  }
`

export const AmenitySubcategory = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  /* Touch-friendly */
  min-height: 23px;
  -webkit-tap-highlight-color: transparent;

  ${media.tabletDown} {
    font-size: 10px;
    gap: 5px;
    min-height: 20px;
  }
`

/**
 * Subtype filters scroll on one line instead of wrapping. Shopping has ten
 * subtypes and Wellness seven, which previously reflowed into four ragged rows
 * and pushed the list below the fold.
 */
/** "107 places · from 157 m" — the category's scale, before any filtering. */
export const AmenityCategorySummary = styled.div`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
  margin-bottom: 6px;

  ${media.tabletDown} {
    font-size: 11px;
    margin-bottom: 5px;
  }
`

export const AmenitySubcategoryScroller = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  /* Sits directly on the card, so it owns its own vertical rhythm. */
  margin-bottom: 8px;
  padding-bottom: 2px;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
  mask-image: linear-gradient(90deg, #000 94%, transparent);

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.light};
    border-radius: 3px;
  }
`

export const AmenitySubcategoryPill = styled.button<{
  $active?: boolean
  $color?: string
}>`
  flex: none;
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  transition:
    background-color 0.15s,
    border-color 0.15s,
    color 0.15s;
  -webkit-tap-highlight-color: transparent;

  border: 1px solid
    ${({ $active, $color, theme }) =>
      $active ? `${$color}66` : theme.colors.border.light};
  background: ${({ $active, $color }) =>
    $active ? `${$color}1f` : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};

  &:hover {
    border-color: ${({ $color }) => $color};
  }

  ${media.tabletDown} {
    font-size: 11px;
    padding: 4px 9px;
  }
`

export const AmenitySubcategoryPillCount = styled.span`
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const AmenityCategoryScore = styled.div`
  text-align: right;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  ${media.tabletDown} {
    gap: 5px;
  }
`

export const AmenityScoreValue = styled.span<{
  variant?: 'low' | 'medium' | 'high' | 'excellent'
}>`
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme, variant }) => {
    switch (variant) {
      case 'excellent':
        return theme.colors.success
      case 'high':
        return theme.colors.success
      case 'medium':
        return theme.colors.warning
      case 'low':
        return theme.colors.danger
      default:
        return theme.colors.text.primary
    }
  }};

  ${media.tabletDown} {
    font-size: 18px;
  }
`

export const AmenityScoreMax = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  ${media.tabletDown} {
    font-size: 12px;
  }
`

// Amenity list
export const AmenityList = styled.div<{ $expanded?: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '600px' : '160px')};
  overflow-y: ${({ $expanded }) => ($expanded ? 'auto' : 'hidden')};
  transition: all 0.3s ease-in-out;
  padding: ${({ $expanded }) => ($expanded ? '8px 10px' : '4px 10px')};
  /*
    No accent rail: it was hardcoded #00ade6, so every category — Healthcare
    included — got a Transport-blue stripe on expand. The chevron and the
    growing list already signal the state.
  */
  background-color: ${({ $expanded, theme }) =>
    $expanded ? theme.colors.background.secondary : 'transparent'};
  /* Smooth scrolling on mobile */
  -webkit-overflow-scrolling: touch;

  ${media.tabletDown} {
    max-height: ${({ $expanded }) => ($expanded ? '500px' : '140px')};
    padding: ${({ $expanded }) => ($expanded ? '8px 10px' : '4px 10px')};
  }
`

export const AmenityItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  gap: 8px;
  /* Touch-friendly tap target */
  min-height: 36px;
  -webkit-tap-highlight-color: transparent;

  &:last-child {
    border-bottom: none;
  }

  ${media.tabletDown} {
    padding: 6px 0;
    gap: 8px;
  }
`

export const AmenityItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const AmenityItemName = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${media.tabletDown} {
    font-size: 11px;
  }
`

export const AmenityItemType = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${media.tabletDown} {
    font-size: 10px;
  }
`

/*
  Line numbers read as objects, not prose — every Swiss transit app renders them
  as small blocks, and a rider scans for "13" faster than for a comma list. One
  neutral colour on purpose: `transit.lines` is a flat array that never says
  which of its entries is the tram and which the bus.
*/
export const AmenityLineBadges = styled.span`
  display: inline-flex;
  gap: 3px;
  vertical-align: middle;
`

export const AmenityLineBadge = styled.span<{ $muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  padding: 0 4px;
  border: 1px solid
    ${({ theme, $muted }) =>
      $muted ? theme.colors.border.light : theme.colors.border.default};
  border-style: ${({ $muted }) => ($muted ? 'dashed' : 'solid')};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme, $muted }) =>
    $muted ? 'transparent' : theme.colors.background.secondary};
  color: ${({ theme, $muted }) =>
    $muted ? theme.colors.text.tertiary : theme.colors.text.secondary};
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  font-variant-numeric: tabular-nums;
`

export const AmenityItemDistance = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 500;
  background: ${({ theme }) => theme.colors.background.tertiary};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.round};

  ${media.tabletDown} {
    font-size: 10px;
    padding: 2px 6px;
  }
`

export const AmenityShowMore = styled.button`
  display: block;
  margin-top: 6px;
  padding: 6px 10px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  /* Touch-friendly */
  min-height: 36px;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    text-decoration: underline;
  }

  /* Disable hover on touch devices */
  @media (hover: none) {
    &:hover {
      text-decoration: none;
    }
    &:active {
      text-decoration: underline;
    }
  }

  ${media.tabletDown} {
    margin-top: 4px;
    padding: 4px 8px;
    font-size: 10px;
  }
`

// Expand/collapse arrow
export const AmenityExpandArrow = styled.div<{ $expanded?: boolean }>`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  transition: transform 0.3s ease;
  margin-left: 6px;
  transform: ${({ $expanded }) =>
    $expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.tabletDown} {
    width: 16px;
    height: 16px;
    margin-left: 5px;
  }
`

// Loading and error states
export const AmenitiesLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 26px;
  gap: 12px;

  ${media.tabletDown} {
    padding: 38px 20px;
    gap: 10px;
  }
`

export const AmenitiesErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 26px;
  gap: 12px;
  text-align: center;

  ${media.tabletDown} {
    padding: 38px 16px;
    gap: 10px;
  }
`

export const ErrorTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;

  ${media.tabletDown} {
    font-size: 13px;
    margin-bottom: 3px;
  }
`

export const AmenitiesErrorMessage = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 20px;
  max-width: 320px;

  ${media.tabletDown} {
    font-size: 11px;
    margin-bottom: 12px;
  }
`

// Data sources
export const DataSources = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: 12px;
  flex-wrap: wrap;

  ${media.tabletDown} {
    font-size: 10px;
    gap: 3px;
    margin-top: 10px;
  }
`

export const AmenitiesSourceLabel = styled.span`
  font-weight: 500;
`

export const SourceList = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`

/**
 * A counted row standing in for a run of unnamed facilities of one type.
 * Reads as "Terrains de sport / 191 à proximité" — the same bold-then-grey
 * anatomy as a named place, so the list keeps one visual grammar.
 */
export const AmenityGroupItem = styled(AmenityItem)<{ $color?: string }>`
  background: ${({ $color }) => `${$color}0d`};
`

export const AmenityGroupCount = styled.span`
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.secondary};
`

/**
 * Outlined rather than filled: the distance is a floor for the whole group
 * ("from 170 m"), not a measurement of one place, and should not read as one.
 */
export const AmenityGroupDistance = styled(AmenityItemDistance)`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`
